<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    enter-from-class="opacity-0 translate-y-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-300 ease-in"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 translate-y-4"
  >
    <div
      v-if="showBanner"
      class="fixed bottom-0 left-0 right-0 z-50 p-4 lg:p-6 consent-banner-wrapper"
      role="dialog"
      aria-labelledby="consent-title"
      aria-describedby="consent-description"
    >
      <div class="max-w-6xl mx-auto bg-white border-2 border-black rounded-lg shadow-lg p-6 lg:p-8">
        <div class="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-8">
          <!-- Content -->
          <div class="flex-1 space-y-3">
            <h2 id="consent-title" class="text-xl font-medium text-black lg:text-2xl">
              {{ t('consent.banner.title') }}
            </h2>
            <p id="consent-description" class="text-sm text-gray-700 lg:text-base">
              {{ t('consent.banner.description') }}
              <NuxtLink
                :to="localePath('privacy')"
                class="text-dark-teal-600 underline hover:text-dark-teal-700"
              >
                {{ t('consent.banner.privacyLink') }}
              </NuxtLink>.
            </p>
          </div>

          <!-- Actions -->
          <div class="flex flex-col gap-3 sm:flex-row lg:flex-shrink-0">
            <!-- Accept Button -->
            <div class="relative inline-block group">
              <div class="absolute inset-0 rounded bg-light-brown border border-black transition-transform duration-150 z-20"></div>
              <div class="absolute inset-0 rounded bg-light-brown-800 border border-black transition-transform duration-150 group-hover:-translate-x-1 group-hover:translate-y-1 z-10"></div>
              <button
                @click="handleAccept"
                class="relative inline-flex rounded no-underline items-center justify-center border border-black transition-all duration-150 group-hover:translate-x-1 group-hover:-translate-y-1 z-30 w-full sm:w-auto h-12 px-6 text-base lg:h-12 lg:px-8 lg:text-base bg-dark-teal-600 text-white"
              >
                {{ t('consent.banner.accept') }}
              </button>
            </div>

            <!-- Reject Button -->
            <div class="relative inline-block group">
              <div class="absolute inset-0 rounded bg-gray-200 border border-black transition-transform duration-150 z-20"></div>
              <div class="absolute inset-0 rounded bg-gray-300 border border-black transition-transform duration-150 group-hover:-translate-x-1 group-hover:translate-y-1 z-10"></div>
              <button
                @click="handleReject"
                class="relative inline-flex rounded no-underline items-center justify-center border border-black transition-all duration-150 group-hover:translate-x-1 group-hover:-translate-y-1 z-30 w-full sm:w-auto h-12 px-6 text-base lg:h-12 lg:px-8 lg:text-base bg-white text-black"
              >
                {{ t('consent.banner.reject') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Settings Toggle (Optional - for advanced users) -->
        <div class="mt-6 pt-6 border-t border-gray-200">
          <button
            @click="showSettings = !showSettings"
            class="text-sm text-gray-600 hover:text-gray-800 underline"
          >
            {{ showSettings ? t('consent.banner.hideSettings') : t('consent.banner.showSettings') }}
          </button>

          <div v-if="showSettings" class="mt-4 space-y-3">
            <label class="flex items-center gap-3">
              <input
                v-model="analyticsEnabled"
                type="checkbox"
                class="w-5 h-5 accent-dark-teal-600"
                @change="handleSettingsChange"
              />
              <div class="flex-1">
                <div class="font-medium text-black">{{ t('consent.banner.analytics.title') }}</div>
                <div class="text-sm text-gray-600">{{ t('consent.banner.analytics.description') }}</div>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
const { t } = useI18n()
const localePath = useLocalePath()
const { hasConsent, requireConsent, grantConsent, revokeConsent } = useConsent()
const config = useRuntimeConfig()

const showBanner = ref(false)
const showSettings = ref(false)
const analyticsEnabled = ref(true)

// Check if banner should be shown
onMounted(() => {
  // Only show if consent is required
  if (requireConsent.value) {
    // Check if consent cookie exists (user has made a choice)
    // null = no choice made yet, false = rejected, true = accepted
    const consentCookie = useCookie<boolean | null>('analytics_consent', { default: () => null })
    
    // Show banner only if user hasn't made a choice yet (cookie is null)
    // Once they accept or reject, we remember their choice and don't show again
    if (consentCookie.value === null) {
      showBanner.value = true
    }
  }
  
  // Initialize analytics preference
  analyticsEnabled.value = hasConsent.value
})

const handleAccept = () => {
  grantConsent()
  showBanner.value = false
}

const handleReject = () => {
  revokeConsent()
  showBanner.value = false
}

const handleSettingsChange = () => {
  if (analyticsEnabled.value) {
    grantConsent()
  } else {
    revokeConsent()
  }
}
</script>

<style scoped>
/* Ensure banner is above other content */
.z-50 {
  z-index: 50;
}

/* Ensure consent banner wrapper creates proper stacking context */
.consent-banner-wrapper {
  position: fixed;
  z-index: 50;
  isolation: isolate; /* Creates new stacking context */
}
</style>


